import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Plus,
  Search,
  MoreHorizontal,
  Activity,
  Trash2,
  Edit,
  Play,
  Pause,
  RefreshCw,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'
import type { Monitor, MonitorType } from '@/types'

const monitorSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.enum(['http', 'tcp', 'dns', 'ssl']),
  url: z.string().url('Invalid URL'),
  interval: z.number().min(30).max(3600),
  timeout: z.number().min(5).max(120),
})

type MonitorFormData = z.infer<typeof monitorSchema>

function AddMonitorDialog() {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const form = useForm<MonitorFormData>({
    resolver: zodResolver(monitorSchema),
    defaultValues: {
      name: '',
      type: 'http',
      url: '',
      interval: 60,
      timeout: 30,
    },
  })

  const addMutation = useMutation({
    mutationFn: (data: MonitorFormData) => apiClient.post<Monitor>('/monitors', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitors'] })
      toast({ title: 'Monitor created', description: 'Monitor has been created successfully' })
      setOpen(false)
      form.reset()
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create monitor', variant: 'destructive' })
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add Monitor
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Monitor</DialogTitle>
          <DialogDescription>
            Create a new monitor to track uptime and performance.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((data) => addMutation.mutate(data))}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" placeholder="My Website" {...form.register('name')} />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={form.watch('type')}
                onValueChange={(value) => form.setValue('type', value as MonitorType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="http">HTTP(S)</SelectItem>
                  <SelectItem value="tcp">TCP</SelectItem>
                  <SelectItem value="dns">DNS</SelectItem>
                  <SelectItem value="ssl">SSL Certificate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="url">URL</Label>
              <Input id="url" placeholder="https://example.com" {...form.register('url')} />
              {form.formState.errors.url && (
                <p className="text-sm text-destructive">{form.formState.errors.url.message}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="interval">Interval (seconds)</Label>
                <Input
                  id="interval"
                  type="number"
                  {...form.register('interval', { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="timeout">Timeout (seconds)</Label>
                <Input
                  id="timeout"
                  type="number"
                  {...form.register('timeout', { valueAsNumber: true })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={addMutation.isPending}>
              {addMutation.isPending ? 'Creating...' : 'Create Monitor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function MonitorCard({ monitor }: { monitor: Monitor }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/monitors/${monitor.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitors'] })
      toast({ title: 'Monitor deleted' })
    },
  })

  const toggleMutation = useMutation({
    mutationFn: () =>
      apiClient.patch(`/monitors/${monitor.id}`, { enabled: !monitor.enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitors'] })
      toast({ title: monitor.enabled ? 'Monitor paused' : 'Monitor resumed' })
    },
  })

  const statusColor = {
    up: 'bg-success',
    down: 'bg-destructive',
    pending: 'bg-warning',
    paused: 'bg-muted',
  }[monitor.status]

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-3">
          <div className={`h-3 w-3 rounded-full ${statusColor}`} />
          <div>
            <CardTitle className="text-base font-medium">{monitor.name}</CardTitle>
            <CardDescription className="text-xs">{monitor.url}</CardDescription>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => toggleMutation.mutate()}>
              {monitor.enabled ? (
                <>
                  <Pause className="mr-2 h-4 w-4" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Resume
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem>
              <RefreshCw className="mr-2 h-4 w-4" />
              Check Now
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => deleteMutation.mutate()}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Badge variant="secondary">{monitor.type.toUpperCase()}</Badge>
            <Badge variant={monitor.status === 'up' ? 'success' : monitor.status === 'down' ? 'destructive' : 'secondary'}>
              {monitor.status}
            </Badge>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            {monitor.responseTime && <span>{monitor.responseTime}ms</span>}
            {monitor.uptime && (
              <span className="ml-2">{monitor.uptime.toFixed(1)}% uptime</span>
            )}
          </div>
        </div>
        {monitor.lastChecked && (
          <p className="text-xs text-muted-foreground mt-2">
            Last checked: {new Date(monitor.lastChecked).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export function MonitorsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['monitors'],
    queryFn: () => apiClient.get<Monitor[]>('/monitors'),
  })

  const monitors = data?.data || []
  const filteredMonitors = monitors.filter((m) => {
    const matchesSearch = m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.url.toLowerCase().includes(search.toLowerCase())
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter
    return matchesSearch && matchesStatus
  })

  return (
    <div className="space-y-6 page-enter">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Monitors</h1>
          <p className="text-muted-foreground">
            Track uptime and performance of your services
          </p>
        </div>
        <AddMonitorDialog />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search monitors..."
            className="pl-9 w-full"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="up">Up</SelectItem>
            <SelectItem value="down">Down</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredMonitors.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Activity className="h-12 w-12 text-muted-foreground mb-4" />
            <CardTitle className="mb-2">No monitors found</CardTitle>
            <CardDescription className="text-center mb-4">
              {search || statusFilter !== 'all'
                ? 'No monitors match your filters'
                : "You haven't created any monitors yet"}
            </CardDescription>
            {!search && statusFilter === 'all' && <AddMonitorDialog />}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {filteredMonitors.map((monitor) => (
            <MonitorCard key={monitor.id} monitor={monitor} />
          ))}
        </div>
      )}
    </div>
  )
}
